if (!customElements.get('byte-color-grid-slider')) {
  class ByteColorGridSlider extends HTMLElement {
    static TABLET_MIN_WIDTH = 750;
    static DESKTOP_MIN_WIDTH = 990;

    constructor() {
      super();

      this.activeIndex = 0;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#updateState();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-color-grid-action]');
      if (!actionTrigger || !this.classList.contains('is-slider')) return;

      const action = actionTrigger.dataset.colorGridAction;

      if (action === 'prev') this.#goToSlide(this.activeIndex - 1);
      if (action === 'next') this.#goToSlide(this.activeIndex + 1);
    }

    handleScroll() {
      if (!this.classList.contains('is-slider')) return;
      this.#syncActiveIndexFromScroll();
    }

    handleResize() {
      this.#updateState();
    }

    #cacheElements() {
      this.track = this.querySelector('[data-color-grid-track]');
      this.slides = this.querySelectorAll('[data-color-grid-slide]');
      this.prevButton = this.querySelector('[data-color-grid-action="prev"]');
      this.nextButton = this.querySelector('[data-color-grid-action="next"]');
      this.controls = this.querySelector('[data-color-grid-controls]');
      this.columnsMobile = Number(this.dataset.columnsMobile) || 2;
      this.columnsTablet = Number(this.dataset.columnsTablet) || 4;
      this.columnsDesktop = Number(this.dataset.columnsDesktop) || 6;
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.track?.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      window.addEventListener('resize', this.boundHandleResize);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.track?.removeEventListener('scroll', this.boundHandleScroll);
      window.removeEventListener('resize', this.boundHandleResize);
    }

    #getVisibleColumns() {
      const viewportWidth = window.innerWidth;

      if (viewportWidth >= ByteColorGridSlider.DESKTOP_MIN_WIDTH) {
        return this.columnsDesktop;
      }

      if (viewportWidth >= ByteColorGridSlider.TABLET_MIN_WIDTH) {
        return this.columnsTablet;
      }

      return this.columnsMobile;
    }

    #updateState() {
      if (!this.track || this.slides.length === 0) return;

      const visibleColumns = this.#getVisibleColumns();
      const shouldEnableSlider = this.slides.length > visibleColumns;

      this.style.setProperty('--byte-color-grid-visible-columns', String(visibleColumns));
      this.classList.toggle('is-slider', shouldEnableSlider);

      if (this.controls) {
        this.controls.hidden = !shouldEnableSlider;
      }

      if (!shouldEnableSlider) {
        this.track.scrollLeft = 0;
        this.activeIndex = 0;
      }

      this.#syncActiveIndexFromScroll();
      this.#updateArrows();
    }

    #syncActiveIndexFromScroll() {
      if (!this.track || this.slides.length === 0 || !this.classList.contains('is-slider')) return;

      const scrollLeft = this.track.scrollLeft;
      let activeIndex = 0;

      for (const [index, slide] of this.slides.entries()) {
        const slideScrollPosition = this.#getSlideScrollPosition(slide);

        if (slideScrollPosition <= scrollLeft + 4) {
          activeIndex = index;
        }
      }

      this.activeIndex = activeIndex;
      this.#updateArrows();
    }

    #getSlideScrollPosition(slide) {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      return this.track.scrollLeft + (slideRect.left - trackRect.left);
    }

    #goToSlide(index) {
      const maxIndex = this.slides.length - 1;
      const targetIndex = Math.min(Math.max(index, 0), maxIndex);
      const targetSlide = this.slides[targetIndex];

      if (!targetSlide || !this.track) return;

      this.activeIndex = targetIndex;
      this.track.scrollTo({
        left: this.#getSlideScrollPosition(targetSlide),
        behavior: 'smooth',
      });
      this.#updateArrows();
    }

    #updateArrows() {
      const maxIndex = this.slides.length - 1;

      if (this.prevButton) {
        this.prevButton.disabled = !this.classList.contains('is-slider') || this.activeIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.classList.contains('is-slider') || this.activeIndex >= maxIndex;
      }
    }
  }

  customElements.define('byte-color-grid-slider', ByteColorGridSlider);
}
